import { config } from '@/core/config'
import { calendarFromApi, calendarToday } from '@/lib/calendarDate'
import type { Contract, ContractListItem, Employee } from '@/api/types'

export function isBlank(value?: string | number | null): boolean {
  if (value === undefined || value === null) return true
  const s = String(value).trim()
  return !s || /^(none|n\/a|na|null|undefined|-|—)$/i.test(s)
}

function uniqueNameParts(parts: Array<string | undefined | null>): string {
  const out: string[] = []
  for (const part of parts) {
    const text = String(part || '').trim()
    if (!text) continue
    const lower = text.toLowerCase()
    if (out.some((x) => x.toLowerCase() === lower)) continue
    if (out.join(' ').toLowerCase().includes(lower)) continue
    out.push(text)
  }
  return out.join(' ')
}

export function employeeFullName(emp: Employee): string {
  const first = emp.empFirstNameEn || emp.empFirstNameAr || ''
  const family = emp.empFamilyNameEn || emp.empLastNameEn || emp.empFamilyNameAr || emp.empLastNameAr || ''
  return uniqueNameParts([first, family]) || '—'
}

export function employeeInitials(emp: Employee): string {
  const name = employeeFullName(emp)
  const bits = name.split(/\s+/).filter(Boolean)
  if (bits.length >= 2) return `${bits[0].charAt(0)}${bits[bits.length - 1].charAt(0)}`.toUpperCase()
  return (bits[0]?.slice(0, 2) || 'NA').toUpperCase()
}

export function displayValue(value?: string | number | null): string {
  if (isBlank(value)) return '—'
  return String(value).trim()
}

export function profileDate(value?: string | null): string {
  if (isBlank(value)) return ''
  const date = new Date(String(value))
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

export function resolveMediaUrl(pathOrUrl?: string | null): string {
  const raw = String(pathOrUrl ?? '').trim()
  if (!raw) return ''
  if (/^https?:\/\//i.test(raw)) return encodeURI(raw)
  if (/^data:/i.test(raw)) return raw
  const normalizedPath = raw.replace(/\\/g, '/').replace(/^\/+/, '')
  const base = String(config.api.baseUrl || '').replace(/\/+$/, '')
  if (!base) return encodeURI(`/${normalizedPath}`)
  return encodeURI(`${base}/${normalizedPath}`)
}

export function formatShiftClock(value?: string): string {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    const match = String(value).match(/(\d{1,2}:\d{2})/)
    return match?.[1] || value
  }
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

export async function settled<T>(promise: Promise<T>, fallback: T): Promise<T> {
  try {
    return await promise
  } catch {
    return fallback
  }
}

function parseContractDay(value?: string | Date | null): string {
  const fromCalendar = calendarFromApi(value)
  if (fromCalendar) return fromCalendar
  if (value == null || value === '') return ''
  const raw = String(value).trim()
  const msJson = raw.match(/\/Date\((-?\d+)/)
  if (msJson) {
    const parsed = new Date(Number(msJson[1]))
    if (!Number.isNaN(parsed.getTime())) return calendarFromApi(parsed)
  }
  const parsed = new Date(raw)
  if (!Number.isNaN(parsed.getTime())) return calendarFromApi(parsed)
  return ''
}

function contractField(item: ContractListItem, ...keys: string[]): unknown {
  const row = item as Record<string, unknown>
  for (const key of keys) {
    if (row[key] !== undefined && row[key] !== null && row[key] !== '') return row[key]
  }
  return undefined
}

function contractStart(item: ContractListItem): string {
  return parseContractDay(
    contractField(item, 'contractStartDate', 'ContractStartDate', 'startDate', 'StartDate') as string | Date | null
  )
}

function contractEnd(item: ContractListItem): string {
  return parseContractDay(
    contractField(item, 'contractEndDate', 'ContractEndDate', 'endDate', 'EndDate') as string | Date | null
  )
}

function contractCoversDay(item: ContractListItem, day: string): boolean {
  const start = contractStart(item)
  const end = contractEnd(item)
  if (start && start > day) return false
  if (end && end < day) return false
  if (!start && !end) return false
  return true
}

function isActiveContract(item: ContractListItem): boolean {
  const statusCode = Number(
    contractField(item, 'contractstatus', 'Contractstatus', 'contractStatus', 'ContractStatus')
  )
  if (statusCode === 1) return true
  const statusText = String(contractField(item, 'status', 'statusName', 'Status', 'StatusName') || '').toLowerCase()
  return statusText === 'active' || statusText.includes('active')
}

function newestContract(a: ContractListItem, b: ContractListItem): number {
  const startCmp = contractStart(b).localeCompare(contractStart(a))
  if (startCmp !== 0) return startCmp
  return Number(b.id || b.contractId || 0) - Number(a.id || a.contractId || 0)
}

/**
 * Active contract for salary: Contractstatus === 1 (same rule as payroll/leave).
 * If several are flagged, prefer the one whose dates include today.
 */
export function pickActiveContractToday(items: ContractListItem[]): ContractListItem | undefined {
  const list = (items || []).filter(Boolean)
  if (list.length === 0) return undefined

  const today = calendarToday()
  const flagged = list.filter(isActiveContract)
  const covering = list.filter((item) => contractCoversDay(item, today))
  const coveringFlagged = flagged.filter((item) => contractCoversDay(item, today))

  const pool =
    coveringFlagged.length > 0 ? coveringFlagged : flagged.length > 0 ? flagged : covering

  if (pool.length === 0) return undefined
  return [...pool].sort(newestContract)[0]
}

function firstAmount(...values: unknown[]): number {
  for (const value of values) {
    if (value === undefined || value === null || value === '') continue
    const amount = Number(value)
    if (!Number.isNaN(amount)) return amount
  }
  return 0
}

function unwrapSalaryPayload(payload: unknown): Record<string, unknown> | null {
  if (!payload || typeof payload !== 'object') return null
  const row = payload as Record<string, unknown>
  const nested = row.data
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    const inner = nested as Record<string, unknown>
    if (
      'basicSalary' in inner ||
      'BasicSalary' in inner ||
      'totalSalary' in inner ||
      'TotalSalary' in inner ||
      'housingAllowance' in inner ||
      'HousingAllowance' in inner
    ) {
      return inner
    }
  }
  return row
}

/** Salary amounts live on EmpSalary, not EmpContract. Merge both (EmpSalary wins). */
export function mergeContractSalary(contract: Contract, salaryPayload?: unknown): Contract {
  const salary = unwrapSalaryPayload(salaryPayload)
  const c = contract as Contract & Record<string, unknown>
  const s = salary || {}

  const basic = firstAmount(s.basicSalary, s.BasicSalary, c.basicSalary, c.BasicSalary)
  const housing = firstAmount(s.housingAllowance, s.HousingAllowance, c.housingAllowance, c.HousingAllowance)
  const transport = firstAmount(
    s.transportationAllowance,
    s.TransportationAllowance,
    s.transpotationAllowance,
    s.TranspotationAllowance,
    c.transpotationAllowance,
    c.TranspotationAllowance,
    c.transportationAllowance
  )
  const meal = firstAmount(s.mealAllowance, s.MealAllowance, c.mealAllowance, c.MealAllowance)
  const other = firstAmount(s.otherAlloance, s.OtherAlloance, s.otherAllowance, s.OtherAllowance, c.otherAllowance, c.OtherAllowance)
  const additional = firstAmount(s.additionalJobSalary, s.AdditionalJobSalary, c.additionalJobSalary, c.AdditionalJobSalary)

  return {
    ...contract,
    basicSalary: basic,
    housingAllowance: housing,
    transpotationAllowance: transport,
    mealAllowance: meal,
    otherAllowance: other,
    additionalJobSalary: additional,
    totalContractSalary: basic + housing + transport + meal + other + additional,
  }
}
